// React
import React, { FC, useState, useRef, useEffect, useContext } from 'react';

// Components
import Dropdown from 'Components/Dropdown';
import {
  RangeDatePicker,
  EngagementDefaultDates,
} from 'Components/RangeDatePicker';

// Assets
import Calendar from 'Assets/ui/analyticscalendar.svg';

// Styles
import injectSheet from 'react-jss';

// Constants
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import { useRouter } from 'Utils/router';
import styleSheet from './styles';

interface Props {
  classes?: any;
  startDate?: string;
  endDate?: string;
  selectedOption?: { label: string; value: number };
  handleCalendarChange?: any;
  handleDropdownChange: (event: any) => void;
  dashBoardName?: string;
}

const defaultValue = {
  label: 'Last 30 days',
  value: -31,
};

const findDateFromDays = (days: any) => {
  const date = new Date();
  date.setDate(date.getDate() + (1 + days));

  let month = `${date.getMonth() + 1}`;
  let day = `${date.getDate()}`;
  const year = date.getFullYear();

  if (month.length < 2) {
    month = `0${month}`;
  }
  if (day.length < 2) {
    day = `0${day}`;
  }
  return `${month}/${day}/${year}`;
};

// This stops the calendar from going more than 2 years back from yesterday
const yearsBackInthePast = -731;

const colorOverride = {
  open: {
    border: {
      color: '#CCD8E2',
    },
    text: {
      color: '#778491',
    },
  },
  close: {
    border: {
      color: '#CCD8E2',
    },
    text: {
      color: '#778491',
    },
  },
};

// the values should be offset by 1, to handle "today"
const lastXDaysDropdownValues = [
  { label: 'Last 7 days', value: -8 },
  { label: 'Last 30 days', value: -31 },
  { label: 'Last 90 days', value: -91 },
  { label: 'Last 365 days', value: -366 },
  { label: 'Custom Date', value: 0 },
];

const { maxDate } = EngagementDefaultDates;

const GoodDataDropdown: FC<Props> = (props: Props) => {
  const {
    classes,
    startDate,
    endDate,
    selectedOption,
    handleCalendarChange,
    handleDropdownChange,
    dashBoardName = '',
  } = props;

  const {
    helpers: { View },
    actions: { track },
  } = useContext(ProductAnalyticsContext);
  const { location } = useRouter();

  const [datePicker, showDatePicker] = useState(false);
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);

  const datePickerRef = useRef(null);

  const handleClickOutside = (event: any) => {
    if (datePicker && !datePickerRef.current.contains(event.target)) {
      showDatePicker(false);
    }
  };

  const dateRangeTrack = (value: string) => {
    if (value === 'Custom Date') return;
    // Combined Analytics event tracking
    track('date_range_changed', {
      screen: `${View(location.pathname)}${
        dashBoardName === '' ? dashBoardName : `-${dashBoardName}` // Add the dash, only if dashBoardName is not an empty string
      }`,
      selected_date_range: value,
    });
  };

  const onDropdownChange = (event: any) => {
    dateRangeTrack(event.target.label);
    if (event.target.label === 'Custom Date') {
      showDatePicker(true);
    }
    handleDropdownChange(event);
  };

  const onCalendarChange = (
    selectedStartDate: string,
    selectedChangeEndDate: string
  ) => {
    dateRangeTrack(
      `Custom Date Range ${selectedStartDate}-${selectedChangeEndDate}`
    );
    handleCalendarChange(selectedStartDate, selectedChangeEndDate);
  };

  const handleCalendarButtonClick = () => {
    showDatePicker(true);
  };

  const handleUserAction = (event: any) => {
    if (event.isOpen === true) {
      setDropdownIsOpen(true);
    } else if (event.isOpen === false) {
      setDropdownIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  return (
    <div className={classes.dropdownContainer} style={{ width: '151px' }}>
      <span className={classes.dropdownTitle}>DATE RANGE</span>
      <Dropdown
        ariaLabel="Select a Range of Days"
        top="50px"
        width={(selectedOption.label === 'Custom Date' && '127.5px') || '151px'}
        items={lastXDaysDropdownValues}
        defaultValue={selectedOption || defaultValue}
        onChange={onDropdownChange}
        onUserAction={handleUserAction}
        colorOverRide={colorOverride}
        fontOverride="Avenir"
      />
      {selectedOption.label === 'Custom Date' && (
        <Calendar
          className={`${classes.calendar} ${datePicker && classes.blue}`}
          onClick={handleCalendarButtonClick}
        />
      )}
      {selectedOption.label === 'Custom Date' && !dropdownIsOpen && (
        <div className={classes.dateDescription}>
          {startDate}
          <span className={classes.dateTo}>to</span>
          {endDate}
        </div>
      )}
      {datePicker && (
        <div
          ref={datePickerRef}
          style={{
            position: 'absolute',
            top: '97px',
            right: '-63px',
            width: '300px',
          }}
        >
          <RangeDatePicker
            startDate={startDate}
            endDate={endDate}
            minDate={findDateFromDays(yearsBackInthePast)}
            maxDate={maxDate}
            onChange={onCalendarChange}
            pickerColor="#CCD8E2"
            pickerBorderWidth="1px"
            bottomRight="#6373A4"
            selectedBackgroundColor="#6373A4"
            selectedColor="#6373A4"
            selectedSpanBackgroundColor="#BCCDEC"
            selectedSpanColor="#BCCDEC"
            inputArrowColor="#778491"
            dateInputColor="#778491"
            anchorDirection="right"
          />
        </div>
      )}
    </div>
  );
};

GoodDataDropdown.defaultProps = {
  classes: {},
  startDate: EngagementDefaultDates.minDate,
  endDate: EngagementDefaultDates.maxDate,
  selectedOption: defaultValue,
  handleCalendarChange: () => {},
  dashBoardName: '',
};

export default injectSheet(styleSheet)(GoodDataDropdown);
